import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Redirect authenticated users to dashboard
  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const features = [
    {
      icon: 'TrendingUp',
      title: 'Marketing Operations',
      description: 'AI-powered content creation, campaign management, and ad analysis to boost your marketing performance.'
    },
    {
      icon: 'CheckSquare',
      title: 'Personal Productivity',
      description: 'Smart task management with priority matrices, deadline tracking, and automated reminders.'
    },
    {
      icon: 'BarChart3',
      title: 'Feedback Analytics',
      description: 'Real-time insights from customer feedback with sentiment analysis and actionable recommendations.'
    },
    {
      icon: 'UserCheck',
      title: 'Onboarding Assistant',
      description: 'Personalized onboarding experiences that adapt to your industry and business goals.'
    },
    {
      icon: 'Users',
      title: 'Manager Agent',
      description: 'Central hub coordinating all agents, managing tasks, and organizing your content library.'
    }
  ];

  const tutorialSteps = [
    {
      step: 1,
      title: 'Create Your Account',
      description: 'Sign up in seconds and choose your plan'
    },
    {
      step: 2,
      title: 'Complete Onboarding',
      description: 'Tell us about your business and goals'
    },
    {
      step: 3,
      title: 'Activate AI Agents',
      description: 'Start using powerful AI agents immediately'
    },
    {
      step: 4,
      title: 'Grow Your Business',
      description: 'Let AI handle tasks while you focus on growth'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="hidden sm:flex"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                Get Started Free
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            AI Agents That Work
            <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              While You Focus on Growth
            </span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            DraftIQ provides intelligent AI agents that handle marketing, productivity, analytics, and more. 
            Automate tasks, gain insights, and scale your business effortlessly.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-lg px-8 py-6"
            >
              <Icon name="Rocket" className="w-5 h-5 mr-2" />
              Start Free Trial
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/pricing')}
              className="text-lg px-8 py-6"
            >
              View Pricing
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            No credit card required • Free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h3 className="text-4xl font-bold text-gray-900 mb-4">Powerful AI Agents</h3>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Five specialized agents working together to transform your business operations
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features?.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Icon name={feature?.icon} className="w-7 h-7 text-purple-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">{feature?.title}</h4>
              <p className="text-gray-600 leading-relaxed">{feature?.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About Us Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">About DraftIQ</h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                DraftIQ is an AI-powered platform designed to help businesses automate operations, 
                improve productivity, and make data-driven decisions. Our suite of specialized AI agents 
                works seamlessly together to handle everything from marketing campaigns to task management.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Built for teams of all sizes, DraftIQ scales with your business. Whether you're a solopreneur 
                or an enterprise, our intelligent agents adapt to your needs and grow with you.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl p-12 flex items-center justify-center">
              <div className="text-center">
                <p className="text-xl font-semibold text-gray-800">
                  Trusted by businesses worldwide
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tutorial Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h3 className="text-4xl font-bold text-gray-900 mb-4">Get Started in Minutes</h3>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Follow our simple 4-step process to unlock the power of AI for your business
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {tutorialSteps?.map((step) => (
            <div key={step?.step} className="relative">
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 h-full">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold mb-4">
                  {step?.step}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{step?.title}</h4>
                <p className="text-gray-600">{step?.description}</p>
              </div>
              {step?.step < 4 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                  <Icon name="ArrowRight" className="w-8 h-8 text-purple-300" />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-purple-600 to-blue-600 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h3>
          <p className="text-xl text-purple-100 mb-8">
            Join thousands of businesses using AI to work smarter, not harder.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-6"
            >
              Get Started Free
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="Zap" className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-bold text-lg">DraftIQ</span>
              </div>
              <p className="text-sm">
                AI-powered business automation platform
              </p>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Product</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/pricing')} className="hover:text-white transition-colors">Pricing</button></li>
                <li><button onClick={() => navigate('/register')} className="hover:text-white transition-colors">Sign Up</button></li>
              </ul>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Company</h5>
              <ul className="space-y-2 text-sm">
                <li><button className="hover:text-white transition-colors">About Us</button></li>
                <li><button className="hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Legal</h5>
              <ul className="space-y-2 text-sm">
                <li><button className="hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button className="hover:text-white transition-colors">Terms of Service</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            <p>© 2026 DraftIQ. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;